import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BlockStack,
  Box,
  Card,
  Checkbox,
  DatePicker,
  Icon,
  InlineGrid,
  InlineStack,
  Popover,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import { CalendarIcon, ClockIcon } from '@shopify/polaris-icons';
import { t } from '../i18n';

export interface CampaignScheduleValue {
  startDate: string; // YYYY-MM-DD
  startTime: string; // HH:mm (24h)
  setEndDate: boolean;
  endDate: string;
  endTime: string;
}

/** Local date YYYY-MM-DD */
export function todayDateInput(): string {
  return toDateInput(new Date());
}

/** Local time HH:mm */
export function nowTimeInput(): string {
  const d = new Date();
  const rounded = Math.floor(d.getMinutes() / 15) * 15;
  d.setMinutes(rounded, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function defaultScheduleValue(): CampaignScheduleValue {
  return {
    startDate: todayDateInput(),
    startTime: nowTimeInput(),
    setEndDate: false,
    endDate: '',
    endTime: '23:45',
  };
}

/** Parse API ISO timestamps into schedule fields */
export function scheduleFromIso(
  startsAt?: string | null,
  endsAt?: string | null,
): CampaignScheduleValue {
  const base = defaultScheduleValue();
  if (startsAt) {
    const d = new Date(startsAt);
    if (!Number.isNaN(d.getTime())) {
      base.startDate = toDateInput(d);
      base.startTime = toTimeInput(d);
    }
  }
  if (endsAt) {
    const d = new Date(endsAt);
    if (!Number.isNaN(d.getTime())) {
      base.setEndDate = true;
      base.endDate = toDateInput(d);
      base.endTime = toTimeInput(d);
    }
  }
  return base;
}

export function scheduleToIso(schedule: CampaignScheduleValue): {
  startsAt: string | null;
  endsAt: string | null;
} {
  const startsAt = combineLocal(schedule.startDate, schedule.startTime);
  const endsAt =
    schedule.setEndDate && schedule.endDate
      ? combineLocal(schedule.endDate, schedule.endTime || '23:59')
      : null;
  return { startsAt, endsAt };
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toTimeInput(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function parseDateInput(value: string): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, day] = value.split('-').map(Number);
  const d = new Date(y, m - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Shopify-style display: Jul 16, 2026 */
function formatDisplayDate(value: string): string {
  const d = parseDateInput(value);
  if (!d) return value;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function combineLocal(date: string, time: string): string | null {
  if (!date) return null;
  const t = time && time.length >= 4 ? time : '00:00';
  const d = new Date(`${date}T${t}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** 15-minute steps — common Shopify schedule time options */
function buildTimeOptions(): Array<{ label: string; value: string }> {
  const opts: Array<{ label: string; value: string }> = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      const label = `${h12}:${String(m).padStart(2, '0')} ${period}`;
      opts.push({ label, value });
    }
  }
  return opts;
}

const TIME_OPTIONS = buildTimeOptions();

function nearestTimeOption(value: string): string {
  if (TIME_OPTIONS.some((o) => o.value === value)) return value;
  const [h, m] = (value || '00:00').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '00:00';
  const rounded = Math.round(m / 15) * 15;
  let hh = h;
  let mm = rounded;
  if (mm === 60) {
    mm = 0;
    hh = (hh + 1) % 24;
  }
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * Shopify Polaris date field: TextField + CalendarIcon + Popover + DatePicker
 * @see https://polaris-react.shopify.com/patterns/date-picking
 */
function ShopifyDateField({
  label,
  value,
  onChange,
  minDate,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  minDate?: string;
}) {
  const selected = parseDateInput(value) || new Date();
  const [{ month, year }, setMonthYear] = useState({
    month: selected.getMonth(),
    year: selected.getFullYear(),
  });
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatDisplayDate(value));
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInputValue(formatDisplayDate(value));
    const d = parseDateInput(value);
    if (d) setMonthYear({ month: d.getMonth(), year: d.getFullYear() });
  }, [value]);

  const disableDatesBefore = useMemo(() => {
    if (!minDate) return undefined;
    const d = parseDateInput(minDate);
    return d || undefined;
  }, [minDate]);

  const handleMonthChange = useCallback((m: number, y: number) => {
    setMonthYear({ month: m, year: y });
  }, []);

  const handleSelect = useCallback(
    (range: { end: Date }) => {
      const next = toDateInput(range.end);
      onChange(next);
      setInputValue(formatDisplayDate(next));
      setOpen(false);
    },
    [onChange],
  );

  /** Allow typing a recognizable date then blur/commit */
  const commitTypedDate = useCallback(() => {
    const parsed = Date.parse(inputValue);
    if (!Number.isNaN(parsed)) {
      const d = new Date(parsed);
      const next = toDateInput(d);
      onChange(next);
      setInputValue(formatDisplayDate(next));
      setMonthYear({ month: d.getMonth(), year: d.getFullYear() });
      return;
    }
    // Reset to last valid value
    setInputValue(formatDisplayDate(value));
  }, [inputValue, onChange, value]);

  return (
    <Popover
      active={open}
      autofocusTarget="none"
      preferredAlignment="left"
      preferInputActivator={false}
      preferredPosition="below"
      preventCloseOnChildOverlayClick
      onClose={() => {
        commitTypedDate();
        setOpen(false);
      }}
      activator={
        <TextField
          role="combobox"
          label={label}
          prefix={<Icon source={CalendarIcon} />}
          value={inputValue}
          onFocus={() => setOpen(true)}
          onChange={setInputValue}
          onBlur={commitTypedDate}
          autoComplete="off"
        />
      }
    >
      <Card padding="400">
        <div ref={datePickerRef}>
          <DatePicker
            month={month}
            year={year}
            selected={selected}
            onMonthChange={handleMonthChange}
            onChange={handleSelect}
            disableDatesBefore={disableDatesBefore}
          />
        </div>
      </Card>
    </Popover>
  );
}

/**
 * Time field using Shopify Select + clock icon (15-min steps).
 */
function ShopifyTimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const safe = nearestTimeOption(value);
  return (
    <BlockStack gap="100">
      <InlineStack gap="100" blockAlign="center">
        <Icon source={ClockIcon} tone="base" />
        <Text as="span" variant="bodyMd">
          {label}
        </Text>
      </InlineStack>
      <Select
        label={label}
        labelHidden
        options={TIME_OPTIONS}
        value={safe}
        onChange={onChange}
      />
    </BlockStack>
  );
}

/**
 * Schedule block for Advanced settings — Shopify Polaris date-picking pattern
 * (Start date / time, optional end date / time with "Set end date").
 */
export function CampaignScheduleFields({
  value,
  onChange,
}: {
  value: CampaignScheduleValue;
  onChange: (next: CampaignScheduleValue) => void;
}) {
  function patch(partial: Partial<CampaignScheduleValue>) {
    onChange({ ...value, ...partial });
  }

  return (
    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">
          {t("Schedule")}
        </Text>
        <Text as="p" tone="subdued" variant="bodySm">
          {t("Choose when this campaign becomes active on your store.")}
        </Text>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
          <ShopifyDateField
            label={t("Start date")}
            value={value.startDate}
            onChange={(startDate) => patch({ startDate })}
          />
          <ShopifyTimeField
            label={t("Start time")}
            value={value.startTime}
            onChange={(startTime) => patch({ startTime })}
          />
        </InlineGrid>

        <Checkbox
          label={t("Set end date")}
          checked={value.setEndDate}
          onChange={(checked) => {
            patch({
              setEndDate: checked,
              endDate:
                checked && !value.endDate ? value.startDate : value.endDate,
              endTime: checked
                ? nearestTimeOption(value.endTime || '23:45')
                : value.endTime,
            });
          }}
        />

        {value.setEndDate && (
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <ShopifyDateField
              label={t("End date")}
              value={value.endDate || value.startDate}
              minDate={value.startDate}
              onChange={(endDate) => patch({ endDate })}
            />
            <ShopifyTimeField
              label={t("End time")}
              value={value.endTime}
              onChange={(endTime) => patch({ endTime })}
            />
          </InlineGrid>
        )}
      </BlockStack>
    </Box>
  );
}
